import { defineStore } from 'pinia'
import {
  buildAddressKey,
  createAddressEntry,
  MAX_RECENT_ADDRESSES,
  readDeliveryAddressStorage,
  writeDeliveryAddressStorage,
} from '@/entities/delivery-address/lib/deliveryAddressStorage'
import { updatePersonalProfileApi } from '@/entities/personal/api/personalApi'
import { useAuthStore } from '@/stores/auth'

function normalizeDeliverable(value, fallback = null) {
  if (typeof value === 'boolean') {
    return value
  }
  return fallback
}

export const useDeliveryAddressStore = defineStore('deliveryAddress', {
  state: () => ({
    address: '',
    addressSubtitle: '',
    location: null,
    deliverable: null,
    recentAddresses: [],
    isSaving: false,
    saveError: null,
    saveErrorCode: null,
    addressRequiredError: false,
  }),
  getters: {
    hasCurrent: (state) => Boolean(state.address?.trim()),
    currentTitle: (state) => state.address,
    isDeliverable: (state) => state.deliverable !== false,
    currentAddressKey() {
      return buildAddressKey({ title: this.address, location: this.location })
    },
    displayRecentAddresses(state) {
      const currentKey = buildAddressKey({ title: state.address, location: state.location })
      return state.recentAddresses.filter((item) => buildAddressKey(item) !== currentKey)
    },
  },
  actions: {
    storageUserId() {
      const userId = useAuthStore().user?.id
      return userId != null && userId !== '' ? String(userId) : null
    },

    isValidLocation(location) {
      if (!location || typeof location !== 'object') return false
      const lat = Number(location.lat)
      const lng = Number(location.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
      return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
    },

    normalizeLocation(location) {
      if (!this.isValidLocation(location)) return null
      return {
        lat: Number(location.lat),
        lng: Number(location.lng),
      }
    },

    getCurrentEntry() {
      if (!this.hasCurrent) {
        return null
      }

      return createAddressEntry({
        title: this.address,
        location: this.location,
        subtitle: this.addressSubtitle,
        deliverable: this.deliverable,
      })
    },

    addToRecent(entry) {
      if (!entry?.title?.trim() || !this.isValidLocation(entry.location)) {
        return
      }

      const key = buildAddressKey(entry)
      const withoutDuplicate = this.recentAddresses.filter((item) => buildAddressKey(item) !== key)

      this.recentAddresses = [{ ...entry, usedAt: Date.now() }, ...withoutDuplicate].slice(
        0,
        MAX_RECENT_ADDRESSES,
      )
    },

    persistToStorage() {
      writeDeliveryAddressStorage(this.storageUserId(), {
        current: this.getCurrentEntry(),
        recent: this.recentAddresses,
      })
    },

    restoreFromStorage() {
      const { current, recent } = readDeliveryAddressStorage(this.storageUserId())
      this.recentAddresses = recent

      if (!useAuthStore().isLoggedIn && current?.title) {
        this.address = current.title
        this.addressSubtitle = current.subtitle ?? ''
        this.location = this.normalizeLocation(current.location)
        this.deliverable = normalizeDeliverable(current.deliverable, null)
      }
    },

    hydrateFromUser(user) {
      this.address = user?.defaultAddress ?? ''
      this.addressSubtitle = String(user?.defaultAddressSubtitle ?? '').trim()
      this.location = this.normalizeLocation(user?.location)
      this.deliverable = normalizeDeliverable(user?.deliverable, null)
      this.restoreFromStorage()
    },

    clear() {
      this.address = ''
      this.addressSubtitle = ''
      this.location = null
      this.deliverable = null
      this.recentAddresses = []
      this.saveError = null
      this.saveErrorCode = null
      this.restoreFromStorage()
    },

    promptAddressRequired(router) {
      this.addressRequiredError = true
      router.push({ name: 'client-address' })
    },

    clearSaveError() {
      this.saveError = null
      this.saveErrorCode = null
    },

    markOutsideDeliveryZone() {
      this.deliverable = false
      this.persistToStorage()
    },

    async confirmSelection(title, location = null, subtitle = '', { deliverable } = {}) {
      const trimmed = String(title ?? '').trim()
      if (!trimmed) {
        return
      }

      const normalizedLocation = this.normalizeLocation(location)
      const normalizedSubtitle = String(subtitle ?? '').trim()
      const nextDeliverable = normalizeDeliverable(deliverable, null)
      const nextKey = buildAddressKey({ title: trimmed, location: normalizedLocation })
      const currentEntry = this.getCurrentEntry()

      if (currentEntry && buildAddressKey(currentEntry) !== nextKey) {
        this.addToRecent(currentEntry)
      }

      this.address = trimmed
      this.addressSubtitle = normalizedSubtitle
      this.location = normalizedLocation
      this.deliverable = nextDeliverable

      if (this.hasCurrent && this.isValidLocation(this.location)) {
        this.addressRequiredError = false
      }

      this.recentAddresses = this.recentAddresses.filter(
        (item) => buildAddressKey(item) !== nextKey,
      )

      this.persistToStorage()

      if (useAuthStore().isLoggedIn) {
        await this.saveToProfile(trimmed)
      }
    },

    async saveToProfile(title) {
      this.isSaving = true
      this.saveError = null
      this.saveErrorCode = null
      try {
        const authStore = useAuthStore()
        const dto = await updatePersonalProfileApi({
          defaultAddress: title,
          defaultAddressSubtitle: this.addressSubtitle,
          location: this.normalizeLocation(this.location) ?? undefined,
        })
        this.deliverable = true
        authStore.setUser({
          ...dto,
          type: dto?.type ?? authStore.user?.type,
          deliverable: typeof dto?.deliverable === 'boolean' ? dto.deliverable : true,
        })
        this.hydrateFromUser(authStore.user)
        this.persistToStorage()
        return authStore.user
      } catch (error) {
        this.saveErrorCode = error?.code ?? null
        this.saveError = error?.message ?? 'Unknown error'
        if (error?.code === 'delivery_outside_zone') {
          this.markOutsideDeliveryZone()
        }
        throw error
      } finally {
        this.isSaving = false
      }
    },
  },
})
